import { __ } from '@wordpress/i18n';
import { useBlockProps, RichText } from '@wordpress/block-editor';
import Inspector from './InspectorControls';
import { getWrapperClasses, getWrapperStyles, getPercent } from './styles';
import {
  useReplayPreview,
  PlayPreviewControl,
} from '../_shared/replay-preview';
import './style.scss';

/**
 * Progress Bar.
 *
 * The editor draws the bar at its final width with no animation: an author
 * setting a value wants to see that value, not wait for a transition on every
 * keystroke. The scroll-triggered fill is a frontend-only enhancement, offered
 * here on demand through the toolbar's play button.
 */
export default function Edit({ attributes, setAttributes }) {
  const { label, value, max, showValue, valueSuffix, animate, duration } =
    attributes;

  const { isPlaying, play } = useReplayPreview(duration);

  const blockProps = useBlockProps({
    className: getWrapperClasses(attributes),
    style: getWrapperStyles(attributes),
  });

  const percent = getPercent(value, max);

  return (
    <>
      {/*
       * No button when the author turned the fill off — pressing play would
       * promise motion the published page will not have.
       */}
      {animate && <PlayPreviewControl isPlaying={isPlaying} onPlay={play} />}

      <Inspector attributes={attributes} setAttributes={setAttributes} />

      <div {...blockProps}>
        <div className="brandy-progress-bar__header">
          <RichText
            tagName="span"
            className="brandy-progress-bar__label"
            value={label}
            onChange={(next) => setAttributes({ label: next })}
            placeholder={__('Label…', 'brandy-blocks')}
            allowedFormats={['core/bold', 'core/italic']}
          />
          {showValue && (
            <span className="brandy-progress-bar__value">
              {value}
              {valueSuffix}
            </span>
          )}
        </div>

        <div className="brandy-progress-bar__track">
          {/*
           * `is-filling` is the frontend's own class, so the keyframe that
           * plays here is the one the visitor gets. It runs from zero to the
           * custom property, which outranks the inline width for as long as
           * it lasts — the bar is back at its real value the moment it ends.
           */}
          <div
            className={`brandy-progress-bar__fill${
              isPlaying ? ' is-filling' : ''
            }`}
            style={{ width: `${percent}%`, transition: 'none' }}
          />
        </div>
      </div>
    </>
  );
}
